// Interface languages (GEN-19, ADR-082): one language for the whole business, set by the owner.
// Arabic is right to left; French and English left to right. A missing key falls back to Arabic.
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import ar from './locales/ar.json';
import fr from './locales/fr.json';
import en from './locales/en.json';

export const languages = ['ar', 'fr', 'en'] as const;
export type Language = (typeof languages)[number];

export const resources = { ar: { translation: ar }, fr: { translation: fr }, en: { translation: en } } as const;

export function isLanguage(value: unknown): value is Language {
  return typeof value === 'string' && (languages as readonly string[]).includes(value);
}

export function directionOf(lang: Language): 'rtl' | 'ltr' {
  return lang === 'ar' ? 'rtl' : 'ltr';
}

/** The owner's business language (ADR-082): every user's default and the language of documents (ADR-110). */
let business: Language = 'ar';
export const businessLanguage = () => business;
export function rememberBusinessLanguage(lang: Language) {
  business = lang;
}

/** Sets <html lang dir> and switches i18next. Called at start-up and when the owner changes the business language. */
export async function applyLanguage(lang: Language, doc: Document = document): Promise<void> {
  doc.documentElement.lang = lang;
  doc.documentElement.dir = directionOf(lang);
  await i18n.changeLanguage(lang);
}

void i18n.use(initReactI18next).init({
  resources,
  lng: 'ar',
  fallbackLng: 'ar',
  keySeparator: false, // keys are flat strings such as "p.round"
  nsSeparator: false,
  interpolation: { escapeValue: false, prefix: '{', suffix: '}' },
  returnNull: false,
});

export default i18n;
